import React, { useEffect } from 'react';
import { Modal, Form, Input, Alert, Divider, Space, Typography, Switch } from 'antd';
import { useTheme } from '@/components/common/ThemeProvider';
import { NubGroupDTO, NubAccessLevelDTO } from '../types';
import { TeamOutlined, SafetyOutlined, KeyOutlined } from '@ant-design/icons';
import { AccessLevelSelector } from './AccessLevelSelector';

const { Text } = Typography;

interface GroupFormDialogProps {
    open: boolean;
    mode: 'view' | 'add' | 'edit' | 'delete';
    initialData?: NubGroupDTO | null;
    accessLevels: NubAccessLevelDTO[];
    selectedAccessLevels: string[];
    onAccessLevelChange: (accessLevels: string[]) => void;
    onCancel: () => void;
    onSubmit: (data: NubGroupDTO) => Promise<void>;
}

export const GroupFormDialog: React.FC<GroupFormDialogProps> = ({
                                                                    open,
                                                                    mode,
                                                                    initialData,
                                                                    accessLevels,
                                                                    selectedAccessLevels,
                                                                    onAccessLevelChange,
                                                                    onCancel,
                                                                    onSubmit,
                                                                }) => {
    const [form] = Form.useForm();
    const { dark } = useTheme();
    const [loading, setLoading] = React.useState(false);

    useEffect(() => {
        if (open && initialData) {
            form.setFieldsValue({
                code: initialData.code,
                name: initialData.name,
                description: initialData.description,
                primary: initialData.primary,
            });
        } else {
            form.resetFields();
        }
    }, [open, initialData, form]);

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();

            if (mode === 'delete') {
                await onSubmit(initialData as NubGroupDTO);
            } else {
                const submitData: NubGroupDTO = {
                    ...values,
                    accessLevels: accessLevels.filter(al =>
                        // @ts-ignore
                        selectedAccessLevels.includes(al.code)
                    )
                };
                await onSubmit(submitData);
            }
        } catch (error) {
            console.error('Form validation failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const isReadOnly = mode === 'view' || mode === 'delete';

    const modalTitle = {
        view: 'View User Group',
        add: 'Create New User Group',
        edit: 'Edit User Group',
        delete: 'Delete User Group',
    }[mode];

    return (
        <Modal
            title={modalTitle}
            open={open}
            onCancel={onCancel}
            onOk={handleSubmit}
            confirmLoading={loading}
            width={1200}
            className={dark ? 'dark' : ''}
        >
            {mode === 'delete' ? (
                <Alert
                    message="Delete User Group"
                    description={
                        <Space direction="vertical">
                            <span>Are you sure you want to delete this user group?</span>
                            <strong>Group: {initialData?.name} ({initialData?.code})</strong>
                            <span>This action cannot be undone and will affect all users assigned to this group.</span>
                        </Space>
                    }
                    type="error"
                    showIcon
                />
            ) : (
                <Form
                    form={form}
                    layout="vertical"
                    disabled={isReadOnly}
                >
                    <Divider orientation="left">Basic Information</Divider>

                    <Form.Item
                        label="Group Code"
                        name="code"
                        rules={[
                            { required: true, message: 'Please input group code' },
                            { min: 3, message: 'Code must be at least 3 characters' },
                            {
                                pattern: /^[A-Z0-9_]+$/,
                                message: 'Code must contain only uppercase letters, numbers, and underscores'
                            }
                        ]}
                    >
                        <Input
                            prefix={<KeyOutlined />}
                            placeholder="Enter group code"
                            readOnly={mode === 'edit'}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Name"
                        name="name"
                        rules={[
                            { required: true, message: 'Please input group name' },
                            { min: 3, message: 'Name must be at least 3 characters' }
                        ]}
                    >
                        <Input
                            prefix={<TeamOutlined />}
                            placeholder="Enter group name"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Description"
                        name="description"
                    >
                        <Input.TextArea
                            placeholder="Enter description"
                            rows={3}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Primary Group"
                        name="primary"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Divider orientation="left">Access Levels</Divider>

                    {isReadOnly ? (
                        <div className="space-y-4">
                            {accessLevels
                                /* @ts-ignore */
                                .filter(al => selectedAccessLevels.includes(al.code))
                                .map(accessLevel => (
                                    <div key={accessLevel.code} className="space-y-2">
                                        <Text strong>{accessLevel.name}</Text>
                                        <div className="pl-4">
                                            <Text type="secondary" className="text-sm block">
                                                {accessLevel.code}
                                            </Text>
                                            {accessLevel.description && (
                                                <div className="text-sm text-gray-500">
                                                    {accessLevel.description}
                                                </div>
                                            )}
                                            {accessLevel.permissions && (
                                                <div className="text-xs text-gray-400 mt-1">
                                                    {accessLevel.permissions.length} permissions
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    ) : (
                        <AccessLevelSelector
                            accessLevels={accessLevels}
                            selectedAccessLevels={selectedAccessLevels}
                            onAccessLevelChange={onAccessLevelChange}
                            disabled={isReadOnly}
                        />
                    )}
                </Form>
            )}
        </Modal>
    );
};